import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppDateInput from "../inputs/AppDateInput";
import AppTextInput from "../inputs/AppTextInput";
import { CreateExpenseRequest } from "../../models/RequestModels/CreateExpenseRequest";
import { ExpenseResponse } from "../../models/ResponseModels/ExpenseResponse";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { useTranslation } from "../../translations/i18n";

interface ExpenseFormModalProps {
  open: boolean;
  loading: boolean;
  properties: PropertyResponse[];
  units: UnitResponse[];
  expense?: ExpenseResponse | null;
  defaultCurrency: string;
  onClose: () => void;
  onSubmit: (body: CreateExpenseRequest) => void;
}

const today = () => new Date().toISOString().split("T")[0];

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const ExpenseFormModal = ({
  open,
  loading,
  properties,
  units,
  expense,
  defaultCurrency,
  onClose,
  onSubmit,
}: ExpenseFormModalProps) => {
  const { t } = useTranslation();

  const [propertyId, setPropertyId] = useState<number | "">("");
  const [unitId, setUnitId] = useState<number | "">("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [expenseDate, setExpenseDate] = useState(today());
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setPropertyId(expense?.property_id || "");
    setUnitId(expense?.unit_id || "");
    setName(expense?.name || "");
    setCategory(expense?.category || "");
    setAmount(expense?.amount ? String(Number(expense.amount)) : "");
    setCurrency(expense?.currency || defaultCurrency);
    setExpenseDate(expense?.expense_date?.split("T")[0] || today());
    setNotes(expense?.notes || "");
  }, [open, expense, defaultCurrency]);

  const filteredUnits = propertyId
    ? units.filter((unit) => unit.property_id === propertyId)
    : units;

  const handlePropertyChange = (value: number | "") => {
    setPropertyId(value);

    if (unitId) {
      const selectedUnit = units.find((unit) => unit.id === unitId);

      if (selectedUnit && value && selectedUnit.property_id !== value) {
        setUnitId("");
      }
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (!name.trim() || !amount || Number(amount) <= 0 || !expenseDate) {
      return;
    }

    onSubmit({
      property_id: propertyId || null,
      unit_id: unitId || null,
      name: name.trim(),
      category: emptyToNull(category),
      amount: Number(amount),
      currency,
      expense_date: expenseDate,
      notes: emptyToNull(notes),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {expense ? t("expenses.editExpense") : t("expenses.newExpense")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <AppTextInput
            label={t("expenses.name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={loading}
            required
            autoFocus
          />

          <AppTextInput
            label={t("expenses.category")}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder={t("expenses.categoryPlaceholder")}
            disabled={loading}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <AppTextInput
              label={t("expenses.amount")}
              value={amount}
              type="number"
              onChange={(event) => setAmount(event.target.value)}
              disabled={loading}
              required
            />

            <AppTextInput
              label={t("booking.currency")}
              value={currency}
              onChange={(event) =>
                setCurrency(event.target.value.toUpperCase())
              }
              disabled={loading}
              required
            />
          </Stack>

          <AppDateInput
            label={t("expenses.date")}
            value={expenseDate}
            onChange={(event) => setExpenseDate(event.target.value)}
            disabled={loading}
            required
          />

          <FormControl fullWidth>
            <InputLabel>{t("properties.title")}</InputLabel>
            <Select
              label={t("properties.title")}
              value={propertyId}
              disabled={loading}
              onChange={(event) =>
                handlePropertyChange(event.target.value as number | "")
              }
            >
              <MenuItem value="">{t("expenses.noProperty")}</MenuItem>
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>
                  {property.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>{t("booking.unit")}</InputLabel>
            <Select
              label={t("booking.unit")}
              value={unitId}
              disabled={loading}
              onChange={(event) => setUnitId(event.target.value as number | "")}
            >
              <MenuItem value="">{t("expenses.noUnit")}</MenuItem>
              {filteredUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.property?.name ? `${unit.property.name} · ` : ""}
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AppTextInput
            label={t("booking.notes")}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={loading}
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={expense ? t("common.update") : t("common.create")}
          fullWidth={false}
          loading={loading}
          disabled={!name.trim() || !amount || Number(amount) <= 0}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseFormModal;
